import React from 'react';
import { Card } from '../components/common/Card';

const MatchmakingInfo = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-app-shell mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-text-primary mb-2">Matchmaking System Documentation</h1>
          <p className="text-text-secondary">Technical reference for the Shuttlr matchmaking algorithm</p>
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-danger-light text-danger-dark border border-danger">
            🔒 Super Admin Only
          </div>
        </div>

        {/* System Overview */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              System Overview
            </h2>
            <div className="space-y-4 text-text-primary">
              <p>
                Shuttlr uses a <strong>two-stage matchmaking system</strong> with a{' '}
                <strong>9-step progressive relaxation</strong> mechanism (strict level 0 + steps 1–9) to create balanced, fair, and enjoyable badminton matches.
              </p>
              <div className="bg-surface-subtle rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Current Configuration</h3>
                <div className="space-y-2 text-sm text-text-primary">
                  <div className="flex items-center justify-between">
                    <span>Scoring System:</span>
                    <span className="px-2 py-1 bg-info-light text-info-dark rounded font-medium">Normalized (0-100 scale)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Relaxation Levels:</span>
                    <span className="px-2 py-1 bg-info-light text-info-dark rounded font-medium">Strict (0) + 9 steps</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Hard Blocks:</span>
                    <span className="px-2 py-1 bg-success-light text-success-dark rounded font-medium">9 categories</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Cooldown:</span>
                    <span className="px-2 py-1 bg-warning-light text-warning-dark rounded font-medium">Session toggle; relaxation step optional</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Normalized Scoring */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Normalized Scoring System
            </h2>
            <div className="space-y-4 text-text-primary">
              <p>
                All scoring dimensions are normalized to a <strong>0-100 scale</strong>, then combined with explicit weights to produce a final score.
              </p>

              <div className="bg-surface-subtle rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Scoring Dimensions &amp; Weights</h3>
                <p className="text-sm text-text-secondary mb-4">
                  Each dimension returns a score 0–100 (or −∞ for hard blocks). Final score = weighted sum; increasing a dimension&apos;s weight makes it matter more.
                </p>
                <div className="space-y-4">

                  {[
                    { label: 'Fairness (35%)', pct: '35%', barColor: 'bg-blue-500', desc: 'Games played balance', detail: [
                      'Compares each player\'s effective games played to the session minimum (minGames).',
                      'Dimension score uses a 40% average + 60% max deviation blend so one high-game player cannot hide behind three at-min teammates.',
                      '100 = all four at min; score drops as composite deviation increases (good ≤0.5, acceptable ≤1.5, poor ≤2.5).',
                      'When session debt is on and scoring-first is off, relative wait debt can nudge the fairness dimension (capped 0–100).',
                      'Additive penalty after weighting: −15 normalized points per game above min, per player (e.g. G:7 when min=5 costs −30).',
                      'Contribution to final = fairness × 0.35, then minus games-above-min penalty.',
                    ]},
                    { label: 'ELO Balance (20%)', pct: '20%', barColor: 'bg-green-500', desc: 'Competitive balance', detail: [
                      'Levels (mens/womens): 60% team average balance + 40% intra-team (partners similar ELO).',
                      'Mixed: 50% team average + 50% gender balance (male vs male, female vs female).',
                      '100 = ELO diff <50; score steps down at 150, 300, 500. No hard block. Contribution = eloBalance × 0.20.',
                    ]},
                    { label: 'Interactions (25%)', pct: '25%', barColor: 'bg-purple-500', desc: 'Partner & court variety (primary variety dimension)', detail: [
                      'Recency-weighted pair history (last 5 personal games, decay 0.7). Partnership pairs are skipped. Base 0–100 from average interaction weight vs thresholds (15% / 25% / 35% / 50%).',
                      'Personal pod overlap (soft): −30 per player with 3/4 of their last doubles court, −65 for 4/4; total pod overlap deduction capped at 100.',
                      'Identical team/sides repeat (soft): −35 on this dimension (exact quartet repeats are handled separately — see below).',
                      '−30 if max personal consecutive court streak ≥ 2; additional −60 if streak ≥ 3.',
                      'Session interaction density: −80 per pair that would interact in nearly every game they play (can hard-block at strict; relaxed at step 4).',
                      'Session player novelty (soft): for each player, average fraction of other on-court players (excl. formal partner) never shared a court with this session (with or against); add avgFraction × 22 (max) to this dimension before pod/consecutive penalties.',
                      'At strict, personal consecutive streak ≥ 2 can hard-block via consecutiveInteractionBlock (threshold 2 = block 3rd consecutive personal game together).',
                      'Separate hard blocks before dimensions: immediate same-team repeat; recent same-team repeat within repeatTeamWindow (default 6 games). Partnerships/scheduled games exempt where configured.',
                      'Contribution to final = interactions × 0.25. Competitive singles default preset uses 20% interactions instead.',
                    ]},
                    { label: 'Game Type (20%)', pct: '20%', barColor: 'bg-yellow-500', desc: 'Balanced matchups + mixed/levels mix', detail: [
                      'Base score: 100 = balanced (mixed vs mixed, mens vs mens); 50 = suboptimal; 0 = mens vs womens.',
                      'Per-player mixed vs levels (or womens vs mixed) balance blended at 60% into the game-type dimension score.',
                      'Under-represented session type bonus: up to +28 on this dimension when mens/mixed/womens is tied for least played (+8 per game behind second-lowest).',
                      'Optional additive deficit boost on final score (outside weights): up to +15 when gameTypeBalance is enabled and session has enough doubles games.',
                      'Contribution = gameType × 0.20.',
                    ]},
                  ].map(({ label, pct, barColor, desc, detail }) => (
                    <div key={label} className="bg-surface rounded-lg p-3 border border-border">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-text-primary font-medium">{label}</span>
                        <span className="text-sm text-text-secondary">{desc}</span>
                      </div>
                      <div className="w-full bg-border-medium rounded-full h-2 mb-2">
                        <div className={`${barColor} h-2 rounded-full`} style={{ width: pct }}></div>
                      </div>
                      <details className="text-sm text-text-primary">
                        <summary className="cursor-pointer font-medium text-text-primary hover:text-primary">How this dimension works</summary>
                        <ul className="mt-2 list-disc list-inside space-y-1 text-text-secondary">
                          {detail.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      </details>
                    </div>
                  ))}

                </div>
              </div>

              <div className="bg-surface-subtle rounded-lg p-4 border border-border">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Variety after the weighted sum</h3>
                <p className="text-sm text-text-secondary mb-3">
                  Quartet novelty and session saturation adjust the final 0–100 combo score <strong>after</strong> the four weighted dimensions. These are not part of the 25% interactions weight.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-text-primary">
                  <li><strong>Session debt (default, scoring-first off):</strong> combo variety debt 0–100 (60% exact new quartet + 40% marginal variety gain) → add <code className="font-mono text-xs">(varietyDebt − 50) × 0.2</code>.</li>
                  <li><strong>Exact same four players again:</strong> −15 on final score, or −45 when session saturation is high (repeat quartet rate ≥ 0.2 or saturation score ≥ 0.25).</li>
                  <li><strong>Novel quartet when saturated:</strong> +30 on final score.</li>
                  <li><strong>Recent load:</strong> penalty when players appeared heavily in the last ~8 games (pacing, not the interactions dimension).</li>
                  <li><strong>Scoring-first mode</strong> (<code className="font-mono text-xs">REACT_APP_SCORING_FIRST_MATCHMAKING</code> / edge <code className="font-mono text-xs">MATCHMAKING_SCORING_FIRST</code>): eligibility-only pool; wait/variety via <code className="font-mono text-xs">applyScoringFirstComboAdjustments</code> (variety debt weight 0.25, same quartet penalties/boosts, recent-load multiplier 10). Structural min-tier guarantees are off.</li>
                </ul>
              </div>

              <div className="bg-info-light border border-info rounded-lg p-4">
                <h4 className="text-info-dark font-semibold mb-2">Pool tiering (before scoring)</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-text-primary">
                  <li><strong>Legacy path:</strong> <code className="px-1 py-0.5 bg-surface border border-border rounded font-mono text-xs">buildEligiblePool</code> prefers min-tier players and can expand using unique game-count tiers; guaranteed min-tier players when expansion is shallow.</li>
                  <li><strong>Session debt model (default on):</strong> wait guarantee uses ceil(courts × 1.5); pool may expand beyond min-tier when waitlist depth, quartet diversity, or same-four-pod risk warrants it — see <code className="font-mono text-xs">sessionDebt.js</code>.</li>
                  <li><strong>Scoring-first:</strong> eligibility-only pool (cooldown, paused, preferences); full legal combo enumeration; fairness/wait/variety from scoring only.</li>
                  <li>Post-score refill with cooldown <strong>off</strong> uses the waitlist only (same as manual &quot;Fill upcoming&quot;); cooldown <strong>on</strong> merges just-finished players when needed.</li>
                </ul>
              </div>

              <div className="bg-info-light border border-info rounded-lg p-4">
                <h4 className="text-info-dark font-semibold mb-2">Why Normalized?</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-text-primary">
                  <li>Old system mixed values across orders of magnitude (100s vs 1000s vs 10,000s)</li>
                  <li>Made tuning weights nearly impossible</li>
                  <li>Normalized system makes all dimensions comparable</li>
                  <li>Easy to understand: 100 = perfect, 50 = acceptable, 0 = poor</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Hard Blocks / Relaxation Levels */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Hard Blocks / Relaxation Levels
            </h2>
            <div className="space-y-4 text-text-primary">
              <p>
                Hard blocks immediately disqualify a matchup. When encountered, the system returns{' '}
                <code className="px-2 py-1 bg-slate-900 text-white rounded font-mono text-xs">-Infinity</code> for that combination.
              </p>
              <p className="text-sm text-text-secondary">
                Partnership splits are enforced first inside <code className="px-1 py-0.5 bg-surface border border-border rounded text-xs font-mono">scoreCombinationNormalized()</code>.
                Hard checks (min+2 guard, immediate/recent same-team repeat, cooldown tiers, game type) run before weighted dimensions.
                Identical team and exact-quartet repeats are <strong>soft-scored</strong> on the interactions dimension and via post-weight quartet penalties — not a separate weighted dimension (debug field <code className="font-mono text-xs">repeatGame</code> is not included in the final sum).
                Scheduled lineups inherit exemptions where implemented in scorer helpers.
              </p>

              <div className="bg-surface-subtle border border-border rounded-lg p-4">
                <h4 className="text-text-primary font-semibold mb-2">How Progressive Relaxation Works</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-text-primary">
                  <li>Try Level <strong>0</strong> (strict) — every enforced constraint is on</li>
                  <li>Still blocked? Relax step <strong>1</strong> — widen category / tier gaps</li>
                  <li>Step <strong>2</strong> — relax cooldown eligibility (respects session cooldown toggle)</li>
                  <li>Step <strong>3</strong> — exact-repeat hard relax (disabled by default; quartet variety is soft-scored)</li>
                  <li>Step <strong>4</strong> — relax session interaction density (pairs who shared most games)</li>
                  <li>Step <strong>5</strong> — allow immediate same-team repeat</li>
                  <li>Step <strong>6</strong> — allow 3rd consecutive personal interaction</li>
                  <li>Step <strong>7</strong> — allow 4th+ consecutive interaction streaks</li>
                  <li>Step <strong>8</strong> — relax min+2 fairness guard (deadlock only; strict until here)</li>
                  <li>Step <strong>9</strong> (last resort) — relax session game-type restrictions</li>
                  <li>Defaults live in <code className="px-1 py-0.5 bg-surface border border-border rounded font-mono text-xs">packages/matchmaking-core/pure/normalizedRelaxation.js</code>; hosts can remap order via presets or stored session JSON</li>
                  <li>Return the surviving combination plus human-readable relaxation reasons</li>
                  <li>All steps exhausted with no scorer → selector returns null</li>
                </ol>
              </div>

              <div className="grid gap-4">
                {[
                  { emoji: '❌', title: '1. Partnership violations', desc: 'Formal partners must land on the same side of the court', relaxable: 'NEVER', relaxColor: 'text-danger-dark', border: 'border-danger', bg: 'bg-danger-light', innerBorder: 'border-danger' },
                  { emoji: '📊', title: '2. Category / tier gap', desc: 'Blocks unreasonable skill deltas when tiers are enforced (baseline cap tightened at strict)', relaxable: 'Relaxation step 1', relaxColor: 'text-success-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '❄️', title: '3. Cooldown observation', desc: 'Requires observing enough court time before inserting a player mid-session', relaxable: 'Relaxation step 2', relaxColor: 'text-success-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '⚖️', title: '4. Min+2 fairness guard', desc: 'Hard-blocks players already at min+2+ unless the game would improve session fairness. Relaxed only at step 8 when no stricter match exists.', relaxable: 'Relaxation step 8 (deadlock only)', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '🔄', title: '5. Quartet & identical-team repeats', desc: 'Personal pod overlap (−30/−65 per player on interactions), identical-team −35, post-weight quartet penalties (−15/−45). All soft at strict — combos can still win if fairness/ELO dominate.', relaxable: 'Soft scoring only (+ optional step 3 if enabled)', relaxColor: 'text-text-secondary', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '👥', title: '6. Session interaction density', desc: 'Blocks pairs who have already shared most of their session games (as partners or opponents), even when those games were not back-to-back.', relaxable: 'Relaxation step 4', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '🔁', title: '7. Immediate same-team repeat', desc: 'Same pair cannot replay on the same side immediately (partnerships exempt where configured).', relaxable: 'Relaxation step 5', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '⏪', title: '7b. Recent same-team repeat', desc: 'Same non-partnership pair on the same team within repeatTeamWindow personal games (default 6). Hard block at strict.', relaxable: 'Via relaxation hardBlocks.allowRecentRepeat', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '🔂', title: '8. 3rd consecutive personal interaction', desc: 'At strict, blocks when either player already shared their last 2 personal games with the other — stopping a 3rd consecutive pairing.', relaxable: 'Relaxation step 6', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '🔂', title: '9. 4th+ consecutive interaction', desc: 'Once step 6 triggers, optionally still block higher streak depths until loosened further.', relaxable: 'Relaxation step 7', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                  { emoji: '🎮', title: '10. Game-type restrictions', desc: 'balanced_only/mixed_only/mens_only etc. from session configuration', relaxable: 'Relaxation step 9 (last resort)', relaxColor: 'text-warning-dark', border: 'border-border', bg: 'bg-surface-subtle', innerBorder: 'border-border' },
                ].map(({ emoji, title, desc, relaxable, relaxColor, border, bg, innerBorder, notePartnershipScheduled }) => (
                  <div key={title} className={`${bg} border-2 ${border} rounded-lg p-4`}>
                    <div className="flex items-start">
                      <span className="text-2xl mr-3">{emoji}</span>
                      <div className="flex-1">
                        <h4 className="text-text-primary font-semibold mb-1">{title}</h4>
                        <p className="text-sm text-text-secondary mb-2">{desc}</p>
                        <div className={`bg-surface border ${innerBorder} rounded p-2 text-xs`}>
                          <strong className="text-text-primary">Relaxable:</strong>{' '}
                          <span className={`${relaxColor} font-bold`}>{relaxable}</span>
                        </div>
                        {notePartnershipScheduled && (
                          <div className="mt-2 text-xs text-text-secondary">
                            Formal partnerships bypass partner-split penalties; scheduled games register as exemptions where implemented in scorer helpers.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* ELO Balance Logic */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Badminton-Specific ELO Balance
            </h2>
            <div className="space-y-4 text-text-primary">
              <p>ELO balancing varies by game type to match badminton-specific considerations:</p>

              <div className="space-y-4">
                {[
                  {
                    title: "Levels Games (Men's/Women's Doubles)",
                    rows: [
                      { label: 'Team Average Balance (60%)', code: 'Team1 Avg ≈ Team2 Avg' },
                      { label: 'Intra-Team Balance (40%)', code: 'Partners similar ELO (±200)' },
                    ],
                    note: 'Example: [1300, 1300] vs [1300, 1300] scores better than [1400, 1200] vs [1400, 1200]',
                  },
                  {
                    title: 'Mixed Doubles',
                    rows: [
                      { label: 'Team Average Balance (50%)', code: 'Team1 Avg ≈ Team2 Avg' },
                      { label: 'Gender-Specific Balance (50%)', code: 'Male1 ≈ Male2, Female1 ≈ Female2' },
                    ],
                    note: 'Example: Strong male + weak female vs Strong male + weak female is balanced',
                  },
                  {
                    title: 'All Types Games',
                    rows: [
                      { label: 'Team Average Balance (100%)', code: 'Team1 Avg ≈ Team2 Avg' },
                    ],
                    note: 'Simplified balancing when game type is unrestricted',
                  },
                ].map(({ title, rows, note }) => (
                  <div key={title} className="bg-surface-subtle border border-border rounded-lg p-4">
                    <h4 className="text-text-primary font-semibold mb-2">{title}</h4>
                    <div className="space-y-2 text-sm">
                      {rows.map(({ label, code }) => (
                        <div key={label} className="flex items-center justify-between">
                          <span className="text-text-primary">{label}</span>
                          <code className="px-2 py-1 bg-surface border border-border rounded text-xs text-text-secondary">{code}</code>
                        </div>
                      ))}
                      <div className="mt-2 text-xs text-text-muted">{note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Feature Flags */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
              Feature Flags &amp; Configuration
            </h2>
            <div className="space-y-4 text-text-primary">
              <p>
                The system supports A/B testing via feature flags in{' '}
                <code className="px-2 py-1 bg-slate-900 text-white rounded font-mono text-xs">packages/matchmaking-core/pure/matchmakingConfig.js</code>
              </p>

              {/* Code block — intentionally always dark */}
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                <div className="text-green-400 mb-2">// packages/matchmaking-core/pure/matchmakingConfig.js</div>
                <div className="text-slate-200">
                  <div>featureFlags: {'{'}</div>
                  <div className="ml-4">useNormalizedScoring: <span className="text-blue-400">true</span>, <span className="text-slate-500">// Use normalized 8-step relaxation</span></div>
                  <div className="ml-4">compareScoringSystems: <span className="text-orange-400">false</span>, <span className="text-slate-500">// Log both for comparison</span></div>
                  <div className="ml-4">varietyAwarePoolSelection: <span className="text-orange-400">false</span>, <span className="text-slate-500">// Legacy pool shaping (off when scoring-first on)</span></div>
                  <div className="ml-4">scoringFirstMatchmaking: <span className="text-orange-400">false</span>, <span className="text-slate-500">// Eligibility-only pool + full-pool scoring</span></div>
                  <div className="ml-4">sessionDebtModel: <span className="text-blue-400">true</span>, <span className="text-slate-500">// Debt math in scoring (legacy structural gates when scoring-first off)</span></div>
                  <div>{'}'}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-success-light border-2 border-success rounded-lg p-4">
                  <h4 className="text-text-primary font-semibold mb-2 text-sm">useNormalizedScoring: true</h4>
                  <ul className="list-disc list-inside space-y-1 text-xs text-text-primary">
                    <li>Uses 9-step progressive relaxation system</li>
                    <li>All hard blocks fully implemented</li>
                    <li>Normalized 0-100 scoring</li>
                    <li>Resolves partnership deadlocks</li>
                  </ul>
                </div>
                <div className="bg-surface-subtle border-2 border-border rounded-lg p-4">
                  <h4 className="text-text-primary font-semibold mb-2 text-sm">useNormalizedScoring: false</h4>
                  <ul className="list-disc list-inside space-y-1 text-xs text-text-secondary">
                    <li>Uses legacy 2-level system</li>
                    <li>Old scoring (mixed magnitudes)</li>
                    <li>STRICT → ALLOW_REPEAT_TEAMS</li>
                    <li>May deadlock on partnerships</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Implementation Files */}
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              Key Implementation Files
            </h2>
            <div className="space-y-2 text-sm">
              {[
                { file: 'normalizedScoring.js',        desc: 'DEFAULT_SCORING_CONFIG weights + dimension scoring + hard-block helpers' },
                { file: 'normalizedRelaxation.js',     desc: '9-step relaxation config + getHardBlocksForLevel()' },
                { file: 'scoringIntegration.js',       desc: 'scoreCombinationNormalized + post-weight variety/session debt' },
                { file: 'sessionDebt.js',              desc: 'Wait debt, combo variety debt, quartet novelty, pool expansion signals' },
                { file: 'relaxationPresets.js',        desc: 'Named presets (strict, flexible, variety-first, etc.)' },
                { file: 'scoreEngine.js',              desc: 'Legacy scoring + feature flag wrapper' },
                { file: 'relaxationOrchestrator.js',   desc: 'evaluateWithNormalizedRelaxation() orchestration' },
                { file: 'scoringFirstMatchmaking.js', desc: 'Scoring-first flag, saturation for scoring, combo debt adjustments' },
                { file: 'twoStageEngine.js',           desc: 'Main entry point + A/B testing router' },
              ].map(({ file, desc }) => (
                <div key={file} className="flex items-start bg-surface-subtle border border-border rounded p-3">
                  <code className="text-primary text-xs font-mono mr-3 mt-0.5">packages/matchmaking-core/pure/{file}</code>
                  <span className="text-text-secondary text-xs flex-1">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-text-secondary text-sm">
          <p>Last updated: July 2026 • Default doubles weights: fairness 35%, ELO 20%, interactions 25%, game type 20%</p>
          <p className="mt-1">For questions, contact the development team</p>
        </div>

      </div>
    </div>
  );
};

export default MatchmakingInfo;
